/**
 * Zerlegt einen Ferienzeitraum in buchbare Wochen (#991).
 *
 * Bewusst reine Funktionen auf `Date` — ohne Angular, ohne Firestore, damit die
 * Randfälle der vier Ferienarten ohne TestBed prüfbar sind.
 */

/** Eine buchbare Woche innerhalb eines Ferienzeitraums. */
export interface GeneratedWeek {
  /** ISO-Kalenderwoche des Wochenbeginns. */
  weekNumber: number;
  /** Erster Betreuungstag der Woche, frühestens der Ferienbeginn. */
  start: Date;
  /** Letzter Betreuungstag der Woche, spätestens das Ferienende. */
  end: Date;
}

/** Letzter Betreuungstag einer Woche: Freitag = Montag + 4. */
const LAST_CARE_DAY_OFFSET = 4;

function atMidnight(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

/** Montag der Woche, in der `date` liegt. */
export function startOfWeek(date: Date): Date {
  const day = atMidnight(date);
  // getDay(): 0 = Sonntag. Der Sonntag gehört zur Woche davor, also -6.
  const offset = day.getDay() === 0 ? -6 : 1 - day.getDay();
  return addDays(day, offset);
}

/** ISO-8601-Kalenderwoche. Die Woche mit dem 4. Januar ist KW 1. */
export function isoWeekNumber(date: Date): number {
  // Auf den Donnerstag derselben Woche schieben — er bestimmt das ISO-Jahr.
  const thursday = atMidnight(date);
  thursday.setDate(thursday.getDate() + 3 - ((thursday.getDay() + 6) % 7));
  const firstThursday = new Date(thursday.getFullYear(), 0, 4);
  const dayDiff =
    (thursday.getTime() - firstThursday.getTime()) / 86_400_000 -
    3 +
    ((firstThursday.getDay() + 6) % 7);
  return 1 + Math.round(dayDiff / 7);
}

/**
 * Zerlegt `[startDate, endDate]` in buchbare Wochen.
 *
 * Iteriert über **Wochengrenzen**, nicht in 7-Tage-Schritten ab dem
 * Ferienbeginn: sonst liegt der Zähler bei mittwochs beginnenden Ferien
 * dauerhaft schief zur Wochengrenze und läuft zwei Tage zu früh aus dem
 * Zeitraum — die letzte Woche der Weihnachtsferien fehlte dadurch komplett
 * (#991).
 *
 * Jede Woche läuft von Montag bis Freitag, gekappt an Ferienanfang und -ende.
 * Die Kappung trägt Ferienzeiträume, die mitten in der Woche beginnen oder
 * enden, unverändert weiter. Wochen ohne Betreuungstag — etwa wenn die Ferien
 * an einem Samstag beginnen — entfallen.
 */
export function generateVacationWeeks(
  startDate: Date,
  endDate: Date
): GeneratedWeek[] {
  const periodStart = atMidnight(startDate);
  const periodEnd = atMidnight(endDate);
  if (periodEnd < periodStart) {
    return [];
  }

  const weeks: GeneratedWeek[] = [];

  for (
    let weekStart = startOfWeek(periodStart);
    weekStart <= periodEnd;
    weekStart = addDays(weekStart, 7)
  ) {
    const lastCareDay = addDays(weekStart, LAST_CARE_DAY_OFFSET);

    const start = weekStart < periodStart ? periodStart : weekStart;
    const end = lastCareDay > periodEnd ? periodEnd : lastCareDay;

    // Kein Betreuungstag in dieser Woche: der Zeitraum beginnt erst am
    // Wochenende oder endet vor dem Montag.
    if (start > end) {
      continue;
    }

    weeks.push({ weekNumber: isoWeekNumber(weekStart), start, end });
  }

  return weeks;
}
